import React, { useEffect, useState } from 'react'
//Components
import Avatar from '../../../../avatar/Avatar'
import DataGrid from '../../../../table/DataGrid'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import FilterStudentsButtom from './FilterStudents/Button'
import useAssessment from '../../../../../hooks/data-entry/useAssessment'
import ResponsiveChips from '../../components/ResponsiveChips/ResponsiveChips'
import { FormControlLabel, Radio } from '@mui/material'
import { useSelector, useDispatch } from 'react-redux'
import { updateSkip } from '../../../../../store/actions/datatable.actions'
import { Alert } from '@mui/material'
import { ASSESSMENT_TYPE_STANDARD, ASSESSMENT_TYPE_CUSTOM } from '../../constants/typeAssessment'

const TableStudents = ({ onChecked, error, assessmentType = ASSESSMENT_TYPE_STANDARD }) => {
  const [included, setIncluded] = useState('all') // all, included, not-included
  const list = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder.studentsList || []
  )
  // MF-532: ahora es un NÚMERO (total del set filtrado). Los ids para "select
  // all" se piden on-demand (getStudentIdsForAssessment), no vienen aquí.
  const totalCount = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder?.totalStudentsCount || 0
  )

  const [selectAllLoading, setSelectAllLoading] = useState(false)

  const loading = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder?.studentsListLoading || false
  )

  const checkedIds = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder?.checkedIds ?? []
  )

  const dispatch = useDispatch()
  const { getStudentsForAssessment, getStudentIdsForAssessment } = useAssessment()

  // MF-503: en STANDARD ("by classes") van TODOS los alumnos de la clase — no se
  // permite selección granular (eso es CUSTOM). La tabla queda de solo-lectura.
  const isStandard = assessmentType === ASSESSMENT_TYPE_STANDARD

  const setIncludedAndResetPage = (value) => {
    dispatch(updateSkip('assessmentStudentsList', 0))
    setIncluded(value)
  }

  const state = useSelector((state) => state?.filter?.assessmentStudentsList || null)
  const skip = useSelector((state) => state?.datatable?.assessmentStudentsList?.skip ?? 0)
  const rowsPerPage = useSelector((state) => state?.datatable?.assessmentStudentsList?.rowsPerPage ?? 50)
  const columnsTable = useSelector((state) => state?.datatable?.assessmentStudentsList?.columnsTable ?? [])

  const selectedTemplate = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder?.template ?? null
  )

  const selectedClassList = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder?.class?.selectedClasses ?? []
  )

  const participantRoles = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder?.participantRoles ?? ['Student']
  )

  // Filtro BASE (year level / classId / roles) SIN show/included/excluded ni
  // paginación. Lo comparten la lista paginada y el "select all" para que ambos
  // resuelvan exactamente el mismo conjunto de estudiantes.
  const buildBaseFilter = () => {
    const filter = {
      yearLevel: selectedTemplate?.yearLevels?.map((yearLevel) => yearLevel._id) || [],
      ...(state?.filter || {})
    }

    if (!filter?.yearLevel || filter?.yearLevelId) {
      filter.yearLevel = selectedTemplate?.yearLevels || []
      filter.yearLevelId = selectedTemplate?.yearLevels?.map((yearLevel) => yearLevel._id) || []
    }

    // Add additional filters for standard assessment type
    if (assessmentType === ASSESSMENT_TYPE_STANDARD) {
      Object.assign(filter, {
        classId: selectedClassList?.filter((el) => el.id !== 'all').map((el) => el.id),
        ...(state?.filter || {})
      })

      //If a class is selected, we delete the year level filter since we need to get the students from that class regardless of year level.
      if (filter.classId?.length > 0) {
        delete filter.yearLevel
      }
    }

    filter.rolesId = participantRoles
    return filter
  }

  useEffect(() => {
    if (assessmentType === ASSESSMENT_TYPE_CUSTOM && !selectedTemplate) {
      setIncluded('all')
    }

    const filter = buildBaseFilter()

    // Apply inclusion filter
    if (included === 'included') {
      filter.show = 'included'
      filter.includedId = checkedIds
    } else if (included === 'not-included') {
      filter.show = 'not-included'
      filter.excludedId = checkedIds
    }

    getStudentsForAssessment(skip, rowsPerPage, filter, assessmentType)
  }, [
    state?.filter,
    rowsPerPage,
    skip,
    selectedTemplate,
    selectedClassList,
    assessmentType,
    included,
    participantRoles
  ])

  const onCheckedAll = async (checked) => {
    if (!checked) {
      onChecked([])
      return
    }
    // MF-532: seleccionar TODO el set filtrado → se piden los ids on-demand
    // (con el filtro base, sin include/exclude), en vez de mantener el array
    // completo en memoria en cada request de la lista.
    setSelectAllLoading(true)
    try {
      const ids = await getStudentIdsForAssessment(buildBaseFilter())
      onChecked(ids.map((id) => String(id)))
    } finally {
      setSelectAllLoading(false)
    }
  }

  const onCheckOne = (id, checked) => {
    if (checked) {
      onChecked([...checkedIds, id])
    } else {
      onChecked(checkedIds?.filter((item) => item !== id && item !== 'all'))
    }
  }

  const shouldDisableFilterStandard =
    (!selectedTemplate || selectedClassList?.length === 0) && assessmentType === ASSESSMENT_TYPE_STANDARD
  const shouldDisableFilterCustom = !selectedTemplate && assessmentType === ASSESSMENT_TYPE_CUSTOM
  const shouldDisableFilter = shouldDisableFilterStandard || shouldDisableFilterCustom

  return (
    <div className='absolute inset-0 bottom-8'>
      <div className='h-16 flex items-center justify-start gap-2'>
        <FilterStudentsButtom
          disabled={shouldDisableFilter || loading}
          dataYears={selectedTemplate?.yearLevels || []}
        />
        <Typography
          variant='inherit'
          className='font-bold capitalize'
          color='primary'
          align='right'
          component='p'
        >
          Show:
        </Typography>
        <div className='flex items-center gap-2'>
          <FormControlLabel
            control={
              <Radio
                checked={included === 'all'}
                name='all'
                value={true}
                onChange={() => setIncludedAndResetPage('all')}
                color='primary'
                disabled={shouldDisableFilter || checkedIds?.length === 0}
              />
            }
            label='All'
          />
          <FormControlLabel
            control={
              <Radio
                checked={included === 'included'}
                name='included'
                value={false}
                onChange={() => setIncludedAndResetPage('included')}
                color='primary'
                disabled={shouldDisableFilter || checkedIds?.length === 0}
              />
            }
            label='Included'
          />
          <FormControlLabel
            control={
              <Radio
                checked={included === 'not-included'}
                name='not-included'
                value={false}
                onChange={() => setIncludedAndResetPage('not-included')}
                color='primary'
                disabled={shouldDisableFilter || checkedIds?.length === 0}
              />
            }
            label='Not included'
          />
        </div>
      </div>
      <div className='top-16 absolute inset-0'>
        <DataGrid
          id='table-user-assessment'
          rows={list.map((row, index) => ({
            ...row,
            index: index + 1,
            avatar: <Avatar size='small' variant='circular' alt={row?.code} src={`photos/${row?.photo}`} />,
            flags: (
              <div className='flex flex-wrap items-center gap-1'>
                {row?.data?.flag?.map((flag, ifl) => (
                  <Chip
                    key={`user-${row._id}-flag-${ifl}`}
                    size='small'
                    label={flag}
                    variant='outlined'
                    color={flag.color}
                    className='text-sm'
                  />
                ))}
              </div>
            ),
            house: (
              <div>
                <Chip
                  key={`user-${row._id}-house`}
                  size='small'
                  variant='outlined'
                  label={
                    <div className='flex items-center gap-1 '>
                      <Typography className='text-xs'>{row?.data?.house}</Typography>
                    </div>
                  }
                />
              </div>
            ),
            form: (
              <div>
                <Chip
                  key={`user-${row._id}-form`}
                  size='small'
                  label={
                    <div className='flex items-center gap-1 text-xs'>
                      <Typography className='text-xs'>{row?.data?.form}</Typography>
                    </div>
                  }
                />
              </div>
            ),
            gender: row?.data?.form,
            yearLevelUser: (
              <div className='flex flex-wrap items-center gap-1'>
                {row?.yearLevel?.map((yearLevel) => (
                  <Chip
                    key={`user-${row._id}-yearLevel-${yearLevel._id}`}
                    size='small'
                    label={yearLevel.name}
                    variant='outlined'
                  />
                ))}
              </div>
            ),
            name: row?.fullName || '',
            code: row?.code || '',
            class: <ResponsiveChips minDisplay={3} maxDisplay={10} items={row?.classes ?? []} field='code' />,
            checkIds: (
              <Checkbox
                id={`checkbox-id-user-${row._id}`}
                checked={isStandard ? true : checkedIds?.includes(row._id)}
                disabled={isStandard}
                onChange={(e) => onCheckOne(row._id, e.target.checked)}
                color='primary'
              />
            )
          }))}
          loading={loading}
          labelNoRows={
            participantRoles.includes('Teacher')
              ? "You don't have any Teachers yet"
              : "You don't have any Students yet"
          }
          nameModule='assessmentStudentsList'
          headers={[...columnsTable]}
          totalRows={totalCount}
          skip={skip}
          rowsPerPage={rowsPerPage}
          actionsSetting={
            <React.Fragment>
              {error && <Alert severity='error'>{error}</Alert>}

              {isStandard ? (
                <Typography className='min-w-100px' align='right' color='text.secondary'>
                  All students in the selected classes are included
                </Typography>
              ) : (
                checkedIds?.length > 0 && (
                  <Typography className='min-w-100px' align='right'>
                    Rows selected {checkedIds.length}
                  </Typography>
                )
              )}
            </React.Fragment>
          }
          columns={[
            {
              label: '#',
              key: 'index',
              align: 'center',
              width: 10,
              show: true,
              hide: false,
              sort: true
            },
            {
              show: true,
              key: 'checkIds',
              align: 'center',
              width: 15,
              label: (
                <Checkbox
                  id={`checkbox-id-user-header`}
                  checked={isStandard ? true : checkedIds?.length > 0 && checkedIds?.length === totalCount}
                  disabled={isStandard || selectAllLoading}
                  indeterminate={isStandard ? false : checkedIds.length > 0 && checkedIds.length < totalCount}
                  onChange={(e) => onCheckedAll(e.target.checked)}
                  color='primary'
                />
              )
            },
            ...columnsTable
          ]}
          orderByField='code'
        />
      </div>
    </div>
  )
}

export default TableStudents
